import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { LinkFooter } from '../../common/typography/typography.styles';
import {
  AppLinksContainer,
  FooterActions,
  FooterColumn,
  FooterTopContainer,
  FooterColumnTitle,
  FooterLogo,
  StoreLink,
  SocialLinksContainer,
} from './footer.styles';
import { isFeatureRequired, isMeest } from '../../utils/client/client.utils';
import { client } from '../../config';
import { FooterLinks } from '../../models/footerLinksModel';
import TrustpilotMini from './helpers/footer-widget';
import { AppRoute } from '../../constants/route';
import { getClientIcons } from '../../assets/social-icons/socialIcons';
import { ReactComponent as GoogleMarketIcon } from '../../assets/google-play-store.svg';
import { ReactComponent as AppleStoreIcon } from '../../assets/apple-store.svg';
import { VisuallyHidden } from '../../common/visually-hidden/visually-hidden.styles';

const vendorLogoSrc = isMeest(client)
  ? '/vendor-specific/meest/meest-logo.svg'
  : '/vendor-specific/polonez/polonez-logo.svg';

interface Props {
  links: FooterLinks | undefined;
}

const FooterTop = ({ links }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isRootRoute = location.pathname === AppRoute.ROOT;

  const iconSet = getClientIcons();
  const omitDownloadApp = isFeatureRequired('hideMobilAppInfo');

  const ourCompanyLinksRequired =
    (isFeatureRequired('aboutUsLinkRequired') && links?.become_agent) ||
    (isFeatureRequired('becomeAgentRequired') && links?.about_us);

  if (!links) return null;

  return (
    <FooterTopContainer>
      <div className='footer-brand'>
        <FooterLogo
          src={vendorLogoSrc}
          alt={client}
        />
        {isFeatureRequired('trustPilotWidgetRequired') && <TrustpilotMini />}
      </div>

      <div className='footer-menu'>
        {ourCompanyLinksRequired && (
          <FooterColumn>
            <FooterColumnTitle>{t('footer_our_company')}</FooterColumnTitle>
            {links?.about_us && isMeest(client) && (
              <LinkFooter href={links?.about_us}>
                {t('footer_about_us')}
              </LinkFooter>
            )}
            {isFeatureRequired('becomeAgentRequired') && (
              <LinkFooter href={links.become_agent}>
                {t('become_an_agent_button')}
              </LinkFooter>
            )}
          </FooterColumn>
        )}

        <FooterColumn>
          <FooterColumnTitle>{t('footer_services')}</FooterColumnTitle>
          {links?.international_shipping && (
            <LinkFooter href={links?.international_shipping}>
              {t('footer_international_shipping')}
            </LinkFooter>
          )}
          {links?.all_services && (
            <LinkFooter href={links?.all_services}>
              {t('footer_all_services')}
            </LinkFooter>
          )}
        </FooterColumn>

        <FooterColumn>
          <FooterColumnTitle>{t('footer_contact_us')}</FooterColumnTitle>
          {(links.customer_support || links.contact_link) && (
            <LinkFooter href={links.customer_support || links.contact_link}>
              {t('footer_customer_support')}
            </LinkFooter>
          )}
          {links.faq && (
            <LinkFooter href={links.faq}>{t('footer_faq')}</LinkFooter>
          )}
        </FooterColumn>
      </div>

      <FooterActions>
        {(links?.play_market || links?.app_store) && (
          <AppLinksContainer $hidden={omitDownloadApp || isRootRoute}>
            <FooterColumnTitle>
              {t('footer_download_mobile_app')}
            </FooterColumnTitle>
            <div className='store-links'>
              {links?.play_market && (
                <StoreLink
                  href={links.play_market}
                  type='google'>
                  <VisuallyHidden>{t('get_it_on_google_pay')}</VisuallyHidden>
                  <GoogleMarketIcon />
                </StoreLink>
              )}
              {links?.app_store && (
                <StoreLink
                  href={links.app_store}
                  type='apple'>
                  <VisuallyHidden>
                    {t('download_on_the_app_store')}
                  </VisuallyHidden>
                  <AppleStoreIcon />
                </StoreLink>
              )}
            </div>
          </AppLinksContainer>
        )}

        <SocialLinksContainer $isHidden={isRootRoute}>
          {Object.entries(links).map(([platform, href]) => {
            const Icon = iconSet[platform as keyof typeof iconSet];
            if (!href || !Icon) return null;

            return (
              <a
                key={platform}
                href={href as string}
                target='_blank'
                rel='noreferrer'>
                <Icon />
                <VisuallyHidden>{t(`${platform} link`)}</VisuallyHidden>
              </a>
            );
          })}
        </SocialLinksContainer>
      </FooterActions>
    </FooterTopContainer>
  );
};

export default observer(FooterTop);
